import { useRef, useState } from 'react'
import { contact } from '../data/site'
import {
  ANALYTICS_EVENTS,
  buildUserProvidedData,
  pushAnalyticsEvent,
  pushGenerateLeadEvent,
} from '../lib/analytics'
import { getMarketingAttribution, inferFormLocation, MARKETING_KEYS } from '../lib/marketing'

const services = [
  ['pilas_de_cimentacion', 'Pilas de cimentación'],
  ['pilotes_prefabricados', 'Pilotes prefabricados'],
  ['tablestacado', 'Tablestacado'],
  ['muros_de_contencion', 'Muros de contención'],
  ['renta_de_maquinaria', 'Renta de maquinaria'],
  ['asesoria_tecnica', 'Asesoría técnica'],
]

const createSubmissionId = () => globalThis.crypto?.randomUUID?.() || `${Date.now()}-${Math.random().toString(36).slice(2)}`

const readResponse = async (response) => {
  try {
    return await response.json()
  } catch {
    return { ok: false }
  }
}

export default function QuoteForm() {
  const [status, setStatus] = useState({ type: 'idle', message: '' })
  const [fieldErrors, setFieldErrors] = useState({})
  const [isSubmitting, setIsSubmitting] = useState(false)
  const [submissionId, setSubmissionId] = useState(createSubmissionId)
  const [attribution] = useState(getMarketingAttribution)
  const hasStarted = useRef(false)
  const validationErrorTracked = useRef(false)

  const pathname = typeof window === 'undefined' ? '' : window.location.pathname
  const formLocation = inferFormLocation(pathname)

  const handleFormStart = () => {
    if (hasStarted.current) return
    hasStarted.current = true
    pushAnalyticsEvent(ANALYTICS_EVENTS.FORM_START, {
      form_id: 'quote_form',
      form_name: 'Solicitud de proyecto',
      form_destination: 'brevo',
      form_location: formLocation,
    })
  }

  const focusFirstError = (form, fields) => {
    const firstField = Object.keys(fields)[0]
    if (!firstField) return
    requestAnimationFrame(() => form.elements.namedItem(firstField)?.focus())
  }

  const handleInvalid = () => {
    if (validationErrorTracked.current) return
    validationErrorTracked.current = true
    pushAnalyticsEvent(ANALYTICS_EVENTS.FORM_SUBMIT_ERROR, {
      form_id: 'quote_form',
      form_location: formLocation,
      error_type: 'client_validation',
    })
  }

  const handleSubmit = async (event) => {
    event.preventDefault()
    if (isSubmitting) return
    const form = event.currentTarget
    if (!form.checkValidity()) {
      form.reportValidity()
      return
    }
    const data = new FormData(form)
    const measurementConsent = true
    const submissionAttribution = getMarketingAttribution()
    const payload = Object.fromEntries(data.entries())
    for (const key of MARKETING_KEYS) payload[key] = submissionAttribution[key] || payload[key] || ''
    payload.landing_page = submissionAttribution.landing_page || payload.landing_page
    payload.first_landing_page = submissionAttribution.first_landing_page || payload.first_landing_page
    payload.referrer = submissionAttribution.referrer || payload.referrer
    payload.privacy_consent = data.get('privacy_consent') === 'on'
    payload.measurement_consent = measurementConsent
    payload.form_id = 'quote_form'
    payload.form_location = formLocation
    payload.submission_id = submissionId

    setIsSubmitting(true)
    setFieldErrors({})
    setStatus({ type: 'loading', message: 'Enviando tu solicitud de forma segura…' })

    try {
      const response = await fetch('/api/leads', {
        method: 'POST',
        headers: { 'content-type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const result = await readResponse(response)
      if (response.status === 422) {
        const errors = result.fields || {}
        setFieldErrors(errors)
        setStatus({ type: 'error', message: result.message || 'Revisa los campos indicados.' })
        pushAnalyticsEvent(ANALYTICS_EVENTS.FORM_SUBMIT_ERROR, {
          form_id: 'quote_form',
          form_location: formLocation,
          error_type: 'server_validation',
        })
        focusFirstError(form, errors)
        return
      }
      if (!response.ok || !result.ok) throw new Error(result.message || 'No se confirmó el envío.')

      const userData = measurementConsent ? await buildUserProvidedData({
        email: payload.email,
        phoneNumber: payload.phone,
        firstName: payload.first_name,
        lastName: payload.last_name,
      }) : undefined
      const marketingParameters = Object.fromEntries(MARKETING_KEYS.map((key) => [
        key,
        measurementConsent || key.startsWith('utm_') ? submissionAttribution[key] : undefined,
      ]))
      pushGenerateLeadEvent({
        lead_id: result.lead_id,
        form_id: 'quote_form',
        form_name: 'Solicitud de proyecto',
        form_location: formLocation,
        lead_source: 'website',
        contact_method: 'email',
        service_type: payload.service,
        landing_page: submissionAttribution.landing_page,
        first_landing_page: submissionAttribution.first_landing_page,
        referrer_domain: submissionAttribution.referrer ? new URL(submissionAttribution.referrer, window.location.origin).hostname : 'direct',
        ...marketingParameters,
      }, userData)

      form.reset()
      setSubmissionId(createSubmissionId())
      hasStarted.current = false
      validationErrorTracked.current = false
      setStatus({
        type: 'success',
        message: `Recibimos tu solicitud. Revisa tu correo para ver la confirmación. Folio: ${result.lead_id}.`,
      })
    } catch (error) {
      pushAnalyticsEvent(ANALYTICS_EVENTS.FORM_SUBMIT_ERROR, {
        form_id: 'quote_form',
        form_location: formLocation,
        error_type: 'delivery_error',
      })
      setStatus({
        type: 'error',
        message: `${error.message} Puedes intentarlo nuevamente o escribirnos por WhatsApp.`,
      })
    } finally {
      setIsSubmitting(false)
    }
  }

  const fieldProps = (name) => ({
    'aria-invalid': Boolean(fieldErrors[name]),
    'aria-describedby': fieldErrors[name] ? `${name}-error` : undefined,
  })

  return (
    <form className="quote-form" onSubmit={handleSubmit} onFocusCapture={handleFormStart} onInvalidCapture={handleInvalid} onInputCapture={() => { validationErrorTracked.current = false }} data-analytics-section="quote_form" noValidate>
      <div className="form-field"><label htmlFor="quote-first-name">Nombre *</label><input id="quote-first-name" name="first_name" autoComplete="given-name" maxLength="80" required {...fieldProps('first_name')} />{fieldErrors.first_name ? <span id="first_name-error" className="form-error">{fieldErrors.first_name}</span> : null}</div>
      <div className="form-field"><label htmlFor="quote-last-name">Apellidos *</label><input id="quote-last-name" name="last_name" autoComplete="family-name" maxLength="100" required {...fieldProps('last_name')} />{fieldErrors.last_name ? <span id="last_name-error" className="form-error">{fieldErrors.last_name}</span> : null}</div>
      <div className="form-field"><label htmlFor="quote-email">Correo electrónico *</label><input id="quote-email" name="email" type="email" autoComplete="email" maxLength="180" required {...fieldProps('email')} />{fieldErrors.email ? <span id="email-error" className="form-error">{fieldErrors.email}</span> : null}</div>
      <div className="form-field"><label htmlFor="quote-phone">Teléfono con clave de país *</label><input id="quote-phone" name="phone" type="tel" inputMode="tel" autoComplete="tel" placeholder="+52 81 1234 5678" maxLength="20" required {...fieldProps('phone')} />{fieldErrors.phone ? <span id="phone-error" className="form-error">{fieldErrors.phone}</span> : null}</div>
      <div className="form-field"><label htmlFor="quote-company">Empresa</label><input id="quote-company" name="company" autoComplete="organization" maxLength="140" /></div>
      <div className="form-field"><label htmlFor="quote-location">Ubicación del proyecto *</label><input id="quote-location" name="location" autoComplete="address-level2" maxLength="180" required {...fieldProps('location')} />{fieldErrors.location ? <span id="location-error" className="form-error">{fieldErrors.location}</span> : null}</div>
      <div className="form-field form-field-wide"><label htmlFor="quote-service">Servicio requerido *</label><select id="quote-service" name="service" required defaultValue="" {...fieldProps('service')}><option value="" disabled>Seleccionar</option>{services.map(([value, label]) => <option value={value} key={value}>{label}</option>)}</select>{fieldErrors.service ? <span id="service-error" className="form-error">{fieldErrors.service}</span> : null}</div>
      <div className="form-field form-field-wide"><label htmlFor="quote-details">Características y tiempos del proyecto *</label><textarea id="quote-details" name="details" rows="5" minLength="20" maxLength="3000" required {...fieldProps('details')} />{fieldErrors.details ? <span id="details-error" className="form-error">{fieldErrors.details}</span> : null}</div>

      {MARKETING_KEYS.map((key) => <input type="hidden" name={key} value={attribution[key] || ''} key={key} />)}
      <input type="hidden" name="landing_page" value={attribution.landing_page || pathname} />
      <input type="hidden" name="first_landing_page" value={attribution.first_landing_page || pathname} />
      <input type="hidden" name="referrer" value={attribution.referrer || ''} />
      <div className="form-honeypot" aria-hidden="true"><label htmlFor="quote-website">Sitio web</label><input id="quote-website" name="website" tabIndex="-1" autoComplete="off" /></div>

      <div className="form-consent form-field-wide">
        <label><input type="checkbox" name="privacy_consent" required {...fieldProps('privacy_consent')} /><span>Acepto el tratamiento de mis datos para atender esta solicitud conforme al <a href="/politica-de-privacidad/" target="_blank" rel="noreferrer">aviso de privacidad</a>. *</span></label>
        {fieldErrors.privacy_consent ? <span id="privacy_consent-error" className="form-error">{fieldErrors.privacy_consent}</span> : null}
      </div>
      <div className="form-submit"><button type="submit" data-analytics-manual="true" disabled={isSubmitting}>{isSubmitting ? 'Enviando…' : 'Enviar solicitud'} <span>↗</span></button><p>Recibirás una confirmación por correo. El equipo CRS revisará la información para continuar contigo.</p></div>
      {status.type !== 'idle' ? <div className={`form-status form-status-${status.type}`} role={status.type === 'error' ? 'alert' : 'status'}>{status.message}{status.type === 'error' ? <> <a href={contact.whatsapp} target="_blank" rel="noreferrer">Abrir WhatsApp</a>.</> : null}</div> : null}
    </form>
  )
}
